import {
  EProgrammingGanttViewMode,
  IProgrammingGanttRow,
  IProgrammingGanttSchedule,
  ProgrammingGantt,
} from "@/components/programming-gantt/programming-gantt";
import $date from "@/utils/date";
import { $ } from "@/utils/selectors";

export class ProgrammingGanttHeaderColumn<
  ROW extends IProgrammingGanttRow,
  SCHEDULE extends IProgrammingGanttSchedule,
> {
  private readonly $container: HTMLElement;
  private readonly $content: HTMLElement;
  private origin: number;

  constructor(
    private readonly gantt: ProgrammingGantt<ROW, SCHEDULE>,
    private x: number = 0,
    private date: Date = new Date(),
    private index: number = 0,
  ) {
    this.$container = document.createElement("div");
    this.$container.classList.add("gantt-header__column");
    this.$container.style.setProperty("--x", `${x}px`);
    this.$container.style.setProperty("--width", `${gantt.getColumnWidth()}px`);
    this.$container.style.setProperty("--height", `${gantt.getOptions().headerHeight}px`);
    $.attr(this.$container, "mode", gantt.getViewMode());
    this.origin = gantt.getVisibleViewport().x + x;

    this.$content = document.createElement("span");

    if (gantt.getViewMode() === EProgrammingGanttViewMode.QUARTER) {
      this.$content.innerHTML = this.gantt.getOptions().formatQuarter(date);
    } else {
      this.$content.innerHTML = this.gantt.getOptions().formatHeaderDate(date);
    }
    this.$container.appendChild(this.$content);

    this.changeDate(date);
    this.changeIndex(index);
  }

  public container() {
    return this.$container;
  }

  public changeIndex(nextIndex: number) {
    this.index = nextIndex;
    this.$container.classList.remove("nth-2", "nth-1");
    this.$container.classList.add(this.index % 2 === 0 ? "nth-2" : "nth-1");
    $.attr(this.$container, "index", nextIndex);
  }

  public isTooFarInRightSide() {
    return this.x > this.gantt.getVisibleViewport().width;
  }

  public isTooFarInLeftSide() {
    const rightPoint = this.x + this.gantt.getColumnWidth();
    return rightPoint < 0;
  }

  public changeDate(nextDate: Date) {
    this.date = $date.clone(nextDate);
    const viewMode = this.gantt.getViewMode();

    if (viewMode === EProgrammingGanttViewMode.WEEK) {
      this.$content.innerHTML = this.gantt.getOptions().formatHeaderDate(nextDate);
    } else if (viewMode === EProgrammingGanttViewMode.MONTH) {
      this.$content.innerHTML = this.gantt.getOptions().formatHeaderMonth(nextDate);
    } else if (viewMode === EProgrammingGanttViewMode.QUARTER) {
      this.$content.innerHTML = this.gantt.getOptions().formatQuarter(nextDate);
    }
  }

  public getDate() {
    return $date.clone(this.date);
  }

  public moveFromOriginBy(number: number) {
    this.x = this.origin + number;
    this.$container.style.setProperty("--x", `${this.x}px`);
  }

  public moveTo(newX: number) {
    this.origin = this.gantt.getVisibleViewport().x + newX;
    this.x = newX;
    this.$container.style.setProperty("--x", `${this.x}px`);
  }

  public getX() {
    return this.x;
  }

  public getIndex() {
    return this.index;
  }
}
